/**
 * Authentification.
 *
 * Deux fournisseurs, et le second ne doit jamais atteindre la production —
 * ce fichier s'en assure lui-même plutôt que de compter sur une consigne.
 */

export type Session = { jeton: string; expire: number; sujet: string };

const CLE = 'zephyr.session';

function lireSession(): Session | null {
  try {
    const brut = sessionStorage.getItem(CLE);
    if (!brut) return null;
    const s = JSON.parse(brut) as Session;
    // 30 s de marge : un jeton qui expire pendant la requête produit une
    // déconnexion incompréhensible pour l'utilisateur.
    return s.expire > Date.now() + 30_000 ? s : null;
  } catch {
    return null;
  }
}

function ecrireSession(s: Session | null): void {
  try {
    if (s) sessionStorage.setItem(CLE, JSON.stringify(s));
    else sessionStorage.removeItem(CLE);
  } catch {
    // Navigation privée, stockage refusé : l'application fonctionne, elle
    // redemandera simplement une connexion au prochain chargement.
  }
}

// ─────────────────────────────────────────────────────────────
// OIDC avec PKCE — le chemin de production
// ─────────────────────────────────────────────────────────────

const b64url = (buf: ArrayBuffer) =>
  btoa(String.fromCharCode(...new Uint8Array(buf)))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

async function empreinte(v: string): Promise<string> {
  return b64url(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(v)));
}

function alea(n = 64): string {
  const o = new Uint8Array(n);
  crypto.getRandomValues(o);
  return b64url(o.buffer);
}

export type ConfigOidc = { issuer: string; clientId: string; redirectUri: string };

export function oidc(config: ConfigOidc) {
  const cleVerif = 'zephyr.pkce';
  const cleEtat = 'zephyr.etat';

  async function connecter(): Promise<never> {
    // PKCE : le secret ne quitte jamais ce navigateur. Un code intercepté
    // dans les journaux d'un proxy ne sert à rien sans le vérificateur.
    const verificateur = alea();
    sessionStorage.setItem(cleVerif, verificateur);
    const etat = alea(16);
    sessionStorage.setItem(cleEtat, etat);

    const u = new URL(`${config.issuer}/protocol/openid-connect/auth`);
    u.searchParams.set('client_id', config.clientId);
    u.searchParams.set('redirect_uri', config.redirectUri);
    u.searchParams.set('response_type', 'code');
    u.searchParams.set('scope', 'openid profile email');
    u.searchParams.set('state', etat);
    u.searchParams.set('code_challenge', await empreinte(verificateur));
    u.searchParams.set('code_challenge_method', 'S256');
    location.assign(u.toString());
    return new Promise<never>(() => undefined);
  }

  async function terminer(): Promise<Session | null> {
    const p = new URLSearchParams(location.search);
    const code = p.get('code');
    if (!code) return null;

    const attendu = sessionStorage.getItem(cleEtat);
    // Sans cette comparaison, n'importe quelle page peut faire aboutir une
    // connexion chez nous avec un code qu'elle a obtenu ailleurs.
    if (!attendu || p.get('state') !== attendu) {
      nettoyerUrl();
      throw new Error('La réponse d’authentification ne correspond pas à la demande.');
    }
    const verificateur = sessionStorage.getItem(cleVerif);
    if (!verificateur) {
      nettoyerUrl();
      throw new Error('La connexion a expiré. Réessayez.');
    }

    const r = await fetch(`${config.issuer}/protocol/openid-connect/token`, {
      method: 'POST',
      headers: { 'content-type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: config.clientId,
        redirect_uri: config.redirectUri,
        code,
        code_verifier: verificateur,
      }),
    });
    sessionStorage.removeItem(cleVerif);
    sessionStorage.removeItem(cleEtat);
    nettoyerUrl();
    if (!r.ok) throw new Error('Le fournisseur d’identité a refusé la connexion.');

    const t = (await r.json()) as { access_token: string; expires_in: number };
    const session: Session = {
      jeton: t.access_token,
      expire: Date.now() + t.expires_in * 1000,
      sujet: sujetDe(t.access_token),
    };
    ecrireSession(session);
    return session;
  }

  return { connecter, terminer, session: lireSession, deconnecter: () => ecrireSession(null) };
}

function nettoyerUrl(): void {
  // Le code d'autorisation ne doit pas rester dans la barre d'adresse,
  // ni dans l'historique, ni dans un Referer.
  history.replaceState({}, '', location.pathname);
}

/** Lecture du `sub` sans vérifier la signature : c'est le serveur qui vérifie. */
function sujetDe(jwt: string): string {
  try {
    const charge = jwt.split('.')[1];
    if (!charge) return '';
    return JSON.parse(atob(charge.replace(/-/g, '+').replace(/_/g, '/'))).sub ?? '';
  } catch {
    return '';
  }
}

// ─────────────────────────────────────────────────────────────
// Jeton fixe — développement et recette UNIQUEMENT
// ─────────────────────────────────────────────────────────────

/**
 * Permet de lancer l'interface sans Keycloak. Le jeton vient de
 * `VITE_JETON_DEV` ou de `?jeton=` dans l'adresse.
 *
 * Le garde-fou ci-dessous n'est pas décoratif : dans un paquet compilé en
 * production, appeler cette fonction lève. Une consigne s'oublie, un
 * `throw` non.
 */
export function jetonFixe(depuisUrl = true) {
  if (import.meta.env.PROD) {
    throw new Error(
      'Le mode « jeton fixe » n’existe pas en production. Configurez VITE_OIDC_ISSUER.',
    );
  }
  const p = new URLSearchParams(location.search);
  const jeton =
    (depuisUrl ? p.get('jeton') : null) ?? (import.meta.env.VITE_JETON_DEV as string | undefined) ?? '';

  return {
    connecter: async (): Promise<Session> => {
      const s: Session = { jeton, expire: Date.now() + 12 * 3600_000, sujet: jeton };
      ecrireSession(s);
      return s;
    },
    terminer: async (): Promise<Session | null> => (jeton ? { jeton, expire: Date.now() + 12 * 3600_000, sujet: jeton } : null),
    session: (): Session | null => (jeton ? { jeton, expire: Date.now() + 12 * 3600_000, sujet: jeton } : lireSession()),
    deconnecter: () => ecrireSession(null),
  };
}

export function choisirFournisseur() {
  const issuer = import.meta.env.VITE_OIDC_ISSUER as string | undefined;
  const clientId = import.meta.env.VITE_OIDC_CLIENT_ID as string | undefined;
  if (issuer && clientId) {
    return oidc({ issuer, clientId, redirectUri: location.origin + '/' });
  }
  // Pas de fournisseur configuré : en production c'est une erreur de
  // déploiement, et jetonFixe() la signalera clairement.
  return jetonFixe();
}
